// Story 6.9 (ADO-1163, AC4) — SideNav / vertical tablist (NEW primitive).
// My Account left navigation (Figma 532:2370).
//
// a11y (NFR24–26): role="tablist" (vertical) with roving focus + Up/Down arrow
// navigation; each tab sets aria-selected and aria-controls its panel id. The
// consuming screen renders the matching role="tabpanel" — build its id +
// aria-labelledby with `sideNavPanelId` / `sideNavTabId` so they align.
//
// Ids are namespaced by the caller-supplied `idPrefix` so multiple SideNav
// instances (or unrelated page elements) never collide on ids like `profile-tab`;
// focus moves via element refs, never a global `getElementById` lookup.

import { useRef } from 'react';
import styles from './SideNav.module.css';

export interface SideNavItem {
  id: string;
  label: string;
}

/** Deterministic tab-button id for a given nav instance + item. */
export const sideNavTabId = (idPrefix: string, id: string): string => `${idPrefix}-${id}-tab`;
/** Deterministic tabpanel id for a given nav instance + item. */
export const sideNavPanelId = (idPrefix: string, id: string): string => `${idPrefix}-${id}-panel`;

export interface SideNavProps {
  label: string;
  items: SideNavItem[];
  activeId: string;
  onChange: (id: string) => void;
  /**
   * Stable, unique prefix for this nav's tab/panel ids — avoids DOM id collisions
   * across instances and with unrelated elements. The consumer must build its
   * tabpanel with `sideNavPanelId(idPrefix, id)` + `aria-labelledby={sideNavTabId(idPrefix, id)}`.
   */
  idPrefix: string;
}

export function SideNav({ label, items, activeId, onChange, idPrefix }: SideNavProps) {
  // Refs to each tab button — focus moves through these, not getElementById, so a
  // colliding id elsewhere on the page can never be focused by mistake.
  const btnRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const move = (dir: 1 | -1) => {
    const idx = items.findIndex((i) => i.id === activeId);
    const next = items[(idx + dir + items.length) % items.length];
    if (!next) return;
    onChange(next.id);
    // Roving focus (NFR24–26): move DOM focus to the newly-selected tab so screen
    // readers announce the change and keyboard focus tracks selection. Works even
    // before the re-render flips `tabIndex`.
    btnRefs.current[next.id]?.focus();
  };

  return (
    <div role="tablist" aria-orientation="vertical" aria-label={label} className={styles.nav}>
      {items.map((item) => {
        const selected = item.id === activeId;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={sideNavTabId(idPrefix, item.id)}
            ref={(el) => {
              btnRefs.current[item.id] = el;
            }}
            aria-selected={selected}
            aria-controls={sideNavPanelId(idPrefix, item.id)}
            tabIndex={selected ? 0 : -1}
            className={styles.tab}
            onClick={() => onChange(item.id)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                move(1);
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                move(-1);
              }
            }}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
